import React from 'react';
import { cn } from '@billda/design-system';

export interface ChipTab {
  label: string;
  value: string;
  count?: number;
}

interface ChipTabsProps {
  tabs: ChipTab[];
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Chip tabs on the DS Tabs boxed variant — the same signature consumers
 * already use. Counts render inside each trigger as a quiet pill.
 */
export const ChipTabs: React.FC<ChipTabsProps> = ({
  tabs,
  value,
  onChange,
  'aria-label': ariaLabel = 'Filters',
  'data-testid': testId,
}) => {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className="flex max-w-full flex-wrap items-center gap-0.5 rounded-lg border border-border-default bg-surface-raised p-0.5"
    >
      {tabs.map(tab => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(tab.value)}
            className={cn(
              'inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-3 type-body-md font-medium text-content-secondary outline-none transition-colors duration-150 hover:text-content-primary focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas',
              selected && 'bg-surface-default text-content-primary shadow-card'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="type-caption ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-surface-selected px-1.5 font-semibold text-content-secondary">
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
